
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Video } from '@/types';
import { videoApi } from '@/services/api.ts';
import VideoCard from '@/components/VideoCard';
import { Button } from '@/components/ui/button';
import { Search, Upload as UploadIcon, Clock, Scissors, Video as VideoIcon } from 'lucide-react';
import SearchBar from '@/components/SearchBar';

const Index = () => {
  const [videos, setVideos] = useState<Video[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  
  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const data = await videoApi.getVideos();
        setVideos(data);
      } catch (error) {
        console.error('Failed to fetch videos:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchVideos();
  }, []);
  
  const handleSearch = (query: string) => {
    navigate(`/search?q=${encodeURIComponent(query)}`);
  };
  
  return (
    <>
      {/* Hero section */}
      <div className="mb-12 animate-fade-in">
        <h1 className="text-4xl font-bold mb-4">Video Intelligence Platform</h1>
        <p className="text-lg text-muted-foreground max-w-2xl mb-6">
          Analyze, search, and extract content from your videos using powerful AI. Find exactly what you need without watching hours of content.
        </p>
        <div className="flex flex-col sm:flex-row gap-4">
          <Button asChild className="transition-all duration-300 hover:shadow-lg hover:translate-y-[-2px]">
            <Link to="/upload">
              <UploadIcon className="w-4 h-4 mr-2" />
              Upload Video
            </Link>
          </Button>
          <Button variant="outline" asChild className="transition-all duration-300 hover:shadow-md">
            <Link to="/search">
              <Search className="w-4 h-4 mr-2" />
              Advanced Search
            </Link>
          </Button>
        </div>
      </div>
      
      {/* Search section */}
      <div className="mb-10 max-w-3xl">
        <SearchBar 
          onSearch={handleSearch}
          placeholder="Search across all your videos..."
          className="animate-fade-in"
        />
      </div>
      
      {/* Video section */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center">
            <VideoIcon className="w-5 h-5 mr-2 text-video-blue" />
            <h2 className="text-2xl font-semibold">Your Videos</h2>
          </div>
          <Button variant="outline" asChild className="hover-scale">
            <Link to="/upload">
              <UploadIcon className="w-4 h-4 mr-2" />
              Upload New
            </Link>
          </Button>
        </div>
        
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {Array(4).fill(0).map((_, i) => (
              <div key={i} className="h-72 rounded-lg bg-muted animate-pulse"></div>
            ))}
          </div>
        ) : videos.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {videos.map((video) => (
              <VideoCard 
                key={video.id} 
                video={video} 
                className="animate-fade-in"
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12 border-2 border-dashed border-muted rounded-lg animate-fade-in">
            <h3 className="text-lg font-medium mb-2">No videos yet</h3>
            <p className="text-muted-foreground mb-4">Upload your first video to get started</p>
            <Button asChild className="hover-scale">
              <Link to="/upload">
                <UploadIcon className="w-4 h-4 mr-2" />
                Upload Video
              </Link>
            </Button>
          </div>
        )}
      </div>
      
      {/* Activity section */}
      <div>
        <div className="flex items-center mb-6">
          <Clock className="w-5 h-5 mr-2 text-video-blue" />
          <h2 className="text-2xl font-semibold">Recent Activity</h2>
        </div>
        <div className="border rounded-lg divide-y">
          <div className="p-4 flex items-center transition-colors hover:bg-muted/50">
            <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center text-muted-foreground">
              <Search className="w-5 h-5" />
            </div>
            <div className="ml-4">
              <p>You searched for <span className="font-medium story-link">object detection</span></p>
              <time className="text-xs text-muted-foreground">2 hours ago</time>
            </div>
          </div>
          <div className="p-4 flex items-center transition-colors hover:bg-muted/50">
            <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center text-muted-foreground">
              <Scissors className="w-5 h-5" />
            </div>
            <div className="ml-4">
              <p>You generated a clip from <span className="font-medium story-link">Introduction to Video Analysis</span></p>
              <time className="text-xs text-muted-foreground">Yesterday</time>
            </div>
          </div>
          <div className="p-4 flex items-center transition-colors hover:bg-muted/50">
            <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center text-muted-foreground">
              <UploadIcon className="w-5 h-5" />
            </div>
            <div className="ml-4">
              <p>You uploaded <span className="font-medium story-link">AI in Video Intelligence</span></p>
              <time className="text-xs text-muted-foreground">3 days ago</time>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Index;
